import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useStorefront, type Currency } from '@/hooks/useStorefront';

export interface VariationOverride {
  price: number;
  currency: Currency;
}

export interface UseVariationOverridesResult {
  overrides: Map<string, VariationOverride>;
  get: (variationId?: string | null) => VariationOverride | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Loads ALL price overrides for the current (non-CH) storefront in a single
 * query and caches for 12h. React-Query is called unconditionally (Rules of
 * Hooks); the network fetch is gated with `enabled: storefront !== 'ch'`, so
 * CH performs ZERO requests and the returned Map is always empty on CH.
 *
 * This eliminates N+1 fetches per rendered price — every DisplayPrice reads
 * from the same cached Map.
 */
export function useVariationOverrides(): UseVariationOverridesResult {
  const { storefront } = useStorefront();
  const enabled = storefront !== 'ch';

  const query = useQuery({
    queryKey: ['variation_overrides', storefront],
    enabled,
    staleTime: 12 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_variation_prices')
        .select('variation_id, price, currency')
        .eq('storefront', storefront);
      if (error) throw error;
      const map = new Map<string, VariationOverride>();
      for (const row of (data as any[]) ?? []) {
        const p = typeof row.price === 'number' ? row.price : Number(row.price);
        if (p > 0 && isFinite(p) && row.variation_id) {
          map.set(row.variation_id as string, { price: p, currency: row.currency as Currency });
        }
      }
      return map;
    },
  });

  const overrides = enabled ? (query.data ?? new Map<string, VariationOverride>()) : new Map();
  return {
    overrides,
    get: (id) => (id ? overrides.get(id) : undefined),
    isLoading: enabled ? query.isLoading : false,
    isError: enabled ? query.isError : false,
  };
}